import React, { useEffect, useState } from "react";
import { MeetingProvider, MeetingConsumer } from "@videosdk.live/react-sdk";
import { authToken } from "../../APIToken";
import { useParams } from "react-router";
import MeetingView from "./components/MeetingView";
import JoiningMeeting from "./components/JoiningMeeting";
import CallEndedScreen from "./components/CallEndedScreen";
import useCallManager from "./hooks/useCallManager";
import useCallTimer from "./hooks/useCallTimer";
import { MAX_PARTICIPANTS } from "./utils/participantManager";

/**
 * Main OnCall component that manages the video call
 * @returns {JSX.Element} OnCall component
 */
const OnCall = () => {
  const params = useParams();
  const {
    callStatus,
    audioConnected,
    participantJoined,
    callError,
    endCall,
    retryAudioConnection,
    ConsultantProfileData,
  } = useCallManager();

  const {
    callDuration,
    formattedTime,
    startCallTimer,
    stopCallTimer,
    formatDuration,
  } = useCallTimer();

  const [showCallEndedScreen, setShowCallEndedScreen] = useState(false);
  const [participantName, setParticipantName] = useState("");

  // Add a class to the body to ensure call UI is on top
  useEffect(() => {
    // Add a class to the body when component mounts
    document.body.classList.add("call-active");

    // Remove the class when component unmounts
    return () => {
      document.body.classList.remove("call-active");
    };
  }, []);

  // Start timer when call becomes active
  useEffect(() => {
    if (callStatus === "active" && audioConnected) {
      startCallTimer();
    } else if (callStatus === "ending" || callStatus === "ended") {
      stopCallTimer();
    }
  }, [callStatus, audioConnected, startCallTimer, stopCallTimer]);

  // Effect to handle call ending transition
  useEffect(() => {
    if (callStatus === "ending") {
      // Show the call ended screen after a short delay
      const timer = setTimeout(() => {
        setShowCallEndedScreen(true);
      }, 1500);

      return () => clearTimeout(timer);
    }

    if (callStatus === "ended") {
      setShowCallEndedScreen(true);
      // endCall();
    }
  }, [callStatus, endCall]);

  // If showing the call ended screen
  if (showCallEndedScreen) {
    return (
      <CallEndedScreen
        callDuration={formatDuration(callDuration)}
        participantName={participantName}
      />
    );
  }

  // If we have auth token and meeting ID, render the meeting
  if (authToken && params?.meetingid) {
    return (
      <div
        className="fixed inset-0 z-[9999]"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
        }}
      >
        {/* Call status overlays */}
        {callStatus === "connecting" && !audioConnected && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-[#A40165]/20 flex items-center justify-center mb-6 animate-pulse">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 text-[#A40165]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 010-7.072m12.728 0l-3.536 3.536m-12.728 0l3.536-3.536"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Connecting...
            </h2>
            <p className="text-gray-600">Establishing secure connection</p>
          </div>
        )}

        {callStatus === "active" && !participantJoined && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-[#A40165]/20 flex items-center justify-center mb-6 animate-pulse">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 text-[#A40165]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Waiting for participant
            </h2>
            <p className="text-gray-600">The other person is joining...</p>
          </div>
        )}

        {callError && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Connection Issue
            </h2>
            <p className="text-gray-600 mb-4">{callError}</p>
            <button
              onClick={retryAudioConnection}
              className="px-4 py-2 bg-[#A40165] text-white rounded-lg hover:bg-[#8A0154] transition-colors"
            >
              Retry Connection
            </button>
          </div>
        )}

        {callStatus === "ending" && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-[#A40165]/20 flex items-center justify-center mb-6 animate-pulse">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 text-[#A40165]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Call Ended
            </h2>
            <p className="text-gray-600">Please wait...</p>
          </div>
        )}

        {/* Call timer display when call is active */}
        {callStatus === "active" && audioConnected && participantJoined && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white/90 px-4 py-2 rounded-full shadow-md z-40 flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-gray-800 font-medium">{formattedTime}</span>
          </div>
        )}

        <MeetingProvider
          config={{
            meetingId: params.meetingid,
            micEnabled: true,
            webcamEnabled: false,
            name: ConsultantProfileData?.name || "Consultant",
            maxParticipants: MAX_PARTICIPANTS, // Limit to 2 participants for one-to-one call
            participantId: ConsultantProfileData?._id, // Use a unique ID to prevent duplicates
          }}
          token={authToken}
        >
          <MeetingConsumer>
            {(meeting) => {
              return (
                <MeetingView
                  onMeetingLeave={endCall}
                  onParticipantNameChange={setParticipantName}
                  meeting={meeting}
                />
              );
            }}
          </MeetingConsumer>
        </MeetingProvider>
      </div>
    );
  }

  // Otherwise show joining screen
  return <JoiningMeeting />;
};

export default OnCall;
